import { useState } from 'react';
import type { BackupStatus } from '../shared/backup';
import { api, UploadAccessError } from './api';
import { ActionLabel } from './DialogLayout';

export function BackupPanel({
  status,
  csrf,
  onAccessError,
}: {
  status: BackupStatus | null;
  csrf: string;
  onAccessError: (error: UploadAccessError) => void;
}) {
  const [setup, setSetup] = useState<{ script: string; manifest: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [replace, setReplace] = useState(false);
  async function prepare() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      setSetup(await api.backupSetup(csrf));
      setReplace(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Backup setup could not be prepared.');
      if (error instanceof UploadAccessError) onAccessError(error);
    } finally {
      setBusy(false);
    }
  }
  function download(name: string, text: string) {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
  const headings = {
    not_connected: 'Not connected yet',
    waiting: 'Waiting for Google Drive',
    copying: 'Backup in progress',
    healthy: 'Photos backed up',
    paused: 'Backups paused',
    error: 'Backup needs attention',
    stale: 'Backup connection missed',
  };
  return (
    <>
      <p className="eyebrow">Family controls</p>
      <h2>Google Drive backups.</h2>
      <div className="backup-status" role="status">
        <strong>{status ? headings[status.state] : 'Checking backup status…'}</strong>
        <p>{status?.message}</p>
        {status?.protectedThrough && (
          <p>Photo feeds synced through {new Date(status.protectedThrough).toLocaleString()}.</p>
        )}
        {status?.lastSuccessAt && (
          <p>Last successful check: {new Date(status.lastSuccessAt).toLocaleString()}.</p>
        )}
        {status?.state === 'copying' && (
          <p>
            {status.copiedFiles} of {status.totalFiles} files checked.
          </p>
        )}
        {status?.freeBytes !== null && status?.freeBytes !== undefined && (
          <p>{(status.freeBytes / 1024 ** 3).toFixed(2)} GB of free Google storage remaining.</p>
        )}
      </div>
      <p>
        The Celebration and Memories with Lloyd have separate folders of original photos you can open and
        download. Copies are checked about every five minutes and pause when space or free usage runs out.
      </p>
      <p className="fine-print">
        Only photos visible in the two feeds are copied. Profile pictures, cover images and Recently Removed
        are excluded. Only a recorded removal through the website can remove its Drive copy. If the server
        loses photos, resets or goes backwards, syncing pauses and Drive copies are kept. Keep the Drive
        folder private.
      </p>
      {status?.folderUrl && (
        <a className="secondary-button" href={status.folderUrl} target="_blank" rel="noreferrer">
          Open backup folder
        </a>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {setup ? (
        <div className="backup-setup">
          <h3>Connect your Google account</h3>
          <ol>
            <li>
              Open a new private project in{' '}
              <a href="https://script.google.com/home/start" target="_blank" rel="noreferrer">
                Google Apps Script
              </a>
              .
            </li>
            <li>Paste the contents of Code.gs into its code editor.</li>
            <li>
              In Project Settings, show the appsscript.json manifest file, then paste the supplied manifest
              there.
            </li>
            <li>Save, select setupBackup and run it. Review Google’s permission request for your account.</li>
          </ol>
          <div className="trash-actions">
            <button className="secondary-button" onClick={() => download('Code.gs', setup.script)}>
              Download Code.gs
            </button>
            <button className="secondary-button" onClick={() => download('appsscript.json', setup.manifest)}>
              Download manifest
            </button>
          </div>
          <p className="fine-print">
            Keep the code private: it contains a key that can read the album’s backup files. Backups are only
            active after Google setup succeeds.
          </p>
        </div>
      ) : replace ? (
        <div className="backup-setup">
          <p>
            Replacing the connection stops the old script from reading new backups. Existing Google Drive
            backups are kept.
          </p>
          <div className="modal-actions">
            <button className="secondary-button" disabled={busy} onClick={() => setReplace(false)}>
              Cancel
            </button>
            <button className="button" disabled={busy} onClick={() => void prepare()}>
              <ActionLabel busy={busy} idle="Replace connection" working="Preparing…" />
            </button>
          </div>
        </div>
      ) : (
        <button
          className="button full-width"
          disabled={busy || !status}
          onClick={() => (status?.state === 'not_connected' ? void prepare() : setReplace(true))}
        >
          <ActionLabel
            busy={busy}
            idle={
              status?.state === 'not_connected' ? 'Set up Google Drive backups' : 'Reconnect Google Drive'
            }
            working="Preparing…"
          />
        </button>
      )}
    </>
  );
}
